/**
 * Загрузка профиля из Битрикс24 и локальное состояние карточки.
 *
 * Профиль читается и пишется напрямую в поле сделки: серверная часть
 * в этом не участвует, поэтому её недоступность не приводит к потере данных
 * (ТЗ п. 8.2).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createEmptyProfile, computeReadiness, computeTotals, type CaseProfile } from '@bd/domain';
import { linkedContact, type CaseContext } from '../../bx24.js';
import { api } from '../../api.js';
import { extractProfile, loadCase, saveProfile as saveToB24, CASE_FIELDS, type CaseItem } from '../../storage/cases.js';
import { applyToProfile, readCrmSnapshot, type FoundValue } from '../../storage/crm-import.js';

/** Профиль из карточки либо пустой — для нового дела. */
function stored(item: CaseItem | null, caseId: string): CaseProfile {
  return extractProfile(item) ?? createEmptyProfile(caseId);
}

export interface ProfileState {
  profile: CaseProfile | null;
  loading: boolean;
  error: string | null;
  /** Есть несохранённые изменения */
  dirty: boolean;
  /** Идентификатор папки дела на Диске, если он уже записан в карточке */
  folderId?: string;
  /** Карточка портала целиком: из неё берётся связанная сделка (FR-06) */
  item?: Record<string, unknown> | null;
  /**
   * Что подставлено из карточки сделки при открытии нового дела.
   * Показывается юристу для проверки: отвечает за документы он.
   */
  prefilled?: FoundValue[];
}

export function useProfile(context: CaseContext) {
  const [state, setState] = useState<ProfileState>({ profile: null, loading: true, error: null, dirty: false });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const item = await loadCase(context.caseId).catch(() => null);
        if (cancelled) return;

        let profile = stored(item, context.caseId);
        let prefilled: FoundValue[] = [];

        /**
         * Новое дело предзаполняется тем, что уже введено в карточке сделки:
         * менеджер заполнил чек-лист, и вводить то же самое второй раз
         * бессмысленно, а главное вредно — два ввода дают два разных ИНН.
         *
         * Только новое дело и только пустые поля: введённое юристом
         * не перезаписывается (FR-06).
         */
        if (!extractProfile(item)) {
          const contact = await linkedContact(item);
          if (contact && !cancelled) {
            profile = {
              ...profile,
              debtor: {
                ...profile.debtor,
                last: contact.last || profile.debtor.last,
                first: contact.first || profile.debtor.first,
                middle: contact.middle || profile.debtor.middle,
                birthDate: contact.birthDate ?? profile.debtor.birthDate,
                phone: contact.phone || profile.debtor.phone,
                email: contact.email || profile.debtor.email,
              },
            };
          }

          const snapshot = await readCrmSnapshot(context.entityTypeId, context.caseId, item);
          if (!cancelled && snapshot.found.length > 0) {
            const applied = applyToProfile(profile, snapshot.found);
            profile = applied.profile;
            prefilled = applied.filled;
          }
        }

        if (cancelled) return;
        setState({
          profile,
          loading: false,
          error: null,
          // Предзаполненное нужно сохранить: иначе оно исчезнет при обновлении
          // страницы, и юрист решит, что приложение его потеряло
          dirty: prefilled.length > 0,
          folderId: (item?.[CASE_FIELDS.folderId] as string) || undefined,
          item,
          prefilled,
        });
      } catch (error) {
        if (cancelled) return;
        // Даже при ошибке чтения даём работать с пустым профилем: иначе
        // пользователь заперт в неработающей вкладке.
        setState({
          profile: createEmptyProfile(context.caseId),
          loading: false,
          error: (error as Error).message,
          dirty: false,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [context.caseId, context.entityTypeId]);

  /** Изменение профиля. Номер редакции растёт при сохранении, не при правке. */
  const update = useCallback((updater: (profile: CaseProfile) => CaseProfile) => {
    setState((current) => {
      if (!current.profile) return current;
      return { ...current, profile: updater(current.profile), dirty: true };
    });
  }, []);

  /**
   * Запись профиля в поле сделки и следом — резервная копия на сервере.
   *
   * Порядок именно такой: портал первичен, и если сервер недоступен, работа
   * не останавливается — теряется только возможность откатиться к предыдущей
   * редакции (ТЗ п. 5.1).
   */
  const save = useCallback(
    async (user?: { id: string; name: string }) => {
      const profile = state.profile;
      if (!profile) return null;

      const next: CaseProfile = {
        ...profile,
        revision: profile.revision + 1,
        meta: {
          ...profile.meta,
          updatedAt: new Date().toISOString(),
          updatedByUserId: user?.id ?? profile.meta.updatedByUserId,
        },
      };

      await saveToB24(context.caseId, next);
      setState((current) => ({ ...current, profile: next, dirty: false }));

      // Резервная копия и запись в журнал: сбой здесь не отменяет сохранения
      await api.backup(context.caseId, next).catch(() => undefined);

      return next;
    },
    [state.profile, context.caseId],
  );

  /**
   * Замена профиля целиком — восстановление из резервной копии (ТЗ п. 5.1).
   *
   * Номер редакции берётся от текущего профиля, а не от восстановленного:
   * иначе редакции пошли бы назад, и следующая копия перезаписала бы ту,
   * из которой только что восстановились.
   */
  const replace = useCallback(
    async (restored: CaseProfile, user?: { id: string; name: string }) => {
      const current = state.profile;
      const next: CaseProfile = {
        ...restored,
        caseId: context.caseId,
        revision: (current?.revision ?? restored.revision) + 1,
        meta: {
          ...restored.meta,
          updatedAt: new Date().toISOString(),
          updatedByUserId: user?.id ?? restored.meta.updatedByUserId,
        },
      };

      await saveToB24(context.caseId, next);
      setState((currentState) => ({ ...currentState, profile: next, dirty: false }));
      await api.backup(context.caseId, next).catch(() => undefined);
      return next;
    },
    [state.profile, context.caseId],
  );

  /** Проверки считаются здесь же: тот же код, что и перед сборкой документа. */
  const readiness = useMemo(
    () => (state.profile ? computeReadiness(state.profile) : null),
    [state.profile],
  );

  const totals = useMemo(() => (state.profile ? computeTotals(state.profile) : null), [state.profile]);

  return { ...state, update, save, replace, readiness, totals, setState };
}
